import type { ChangeEvent } from "react";
import Select from "react-select";
import {
  FieldLabel,
  FormSection,
  descriptionSelectStyles,
} from "../../../../../components/propertyDescription/descriptionUi";
import OwnerEmailSearchField from "../../../../../components/propertyDescription/OwnerEmailSearchField";
import KeyLocationFields from "../../../../../components/propertyDescription/KeyLocationFields";
import type { KeyLocationCatalogItem } from "../../../../../services/propertyKeyLocation";
import { PropertyAlternativeContactFields } from "../../../../../components/propertyDescription/PropertyAlternativeContactFields";

type OwnerOption = { id: number; email: string };
type SelectOption = { label: string; value: number };

type Props = {
  hasSelectedOwner: boolean;
  ownerInputValue: string;
  ownerName?: string;
  ownerPhone?: string;
  isFetchingOwners: boolean;
  isOwnerDropdownOpen: boolean;
  owners: OwnerOption[];
  consultantsOptions: SelectOption[];
  consultantValue: SelectOption | undefined;
  isFetchingConsultants: boolean;
  findersOptions: SelectOption[];
  finderValue: SelectOption | undefined;
  loadingFinders: boolean;
  onOwnerSearchChange: (event: ChangeEvent<HTMLInputElement>) => string;
  onOwnerSearchTermChange: (value: string) => void;
  onSelectOwner: (id: number) => void;
  onClearOwner: () => void;
  onOpenNewOwnerModal: () => void;
  onCloseOwnerDropdown: () => void;
  onConsultantChange: (id: number | undefined) => void;
  onFinderChange: (id: number | undefined) => void;
  keyLocationCatalog: KeyLocationCatalogItem[];
  keyLocationId?: number | null;
  keyLocationNote: string;
  keyLocationPassword: string;
  onKeyLocationChange: (id: number | null) => void;
  onKeyLocationNoteChange: (value: string) => void;
  onKeyLocationPasswordChange: (value: string) => void;
  contactEmail: string;
  contactPhone: string;
  contactEmailError?: string;
  contactPhoneError?: string;
  onContactEmailChange: (value: string) => void;
  onContactPhoneChange: (value: string) => void;
};

export default function ResponsiblesSection({
  hasSelectedOwner,
  ownerInputValue,
  ownerName,
  ownerPhone,
  isFetchingOwners,
  isOwnerDropdownOpen,
  owners,
  consultantsOptions,
  consultantValue,
  isFetchingConsultants,
  findersOptions,
  finderValue,
  loadingFinders,
  onOwnerSearchChange,
  onOwnerSearchTermChange,
  onSelectOwner,
  onClearOwner,
  onOpenNewOwnerModal,
  onCloseOwnerDropdown,
  onConsultantChange,
  onFinderChange,
  keyLocationCatalog,
  keyLocationId,
  keyLocationNote,
  keyLocationPassword,
  onKeyLocationChange,
  onKeyLocationNoteChange,
  onKeyLocationPasswordChange,
  contactEmail,
  contactPhone,
  contactEmailError,
  contactPhoneError,
  onContactEmailChange,
  onContactPhoneChange,
}: Props) {
  return (
    <FormSection
      title="Responsáveis"
      description="Busque o e-mail do proprietário ou cadastre um novo."
    >
      <FieldLabel>Proprietário</FieldLabel>
      <OwnerEmailSearchField
        hasSelectedOwner={hasSelectedOwner}
        inputValue={ownerInputValue}
        isFetching={isFetchingOwners}
        isDropdownOpen={isOwnerDropdownOpen}
        owners={owners}
        onInputChange={(e) => {
          onOwnerSearchTermChange(onOwnerSearchChange(e));
        }}
        onSelectOwner={onSelectOwner}
        onClearOwner={onClearOwner}
        onCloseDropdown={onCloseOwnerDropdown}
        onCreateOwner={onOpenNewOwnerModal}
      />

      {hasSelectedOwner ? (
        <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Conta do proprietário
          </p>
          <p className="mt-1 font-medium text-slate-900">
            {ownerName || "Proprietário selecionado"}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {[ownerPhone, ownerInputValue].filter(Boolean).join(" · ")}
          </p>
        </div>
      ) : null}

      <PropertyAlternativeContactFields
        contactEmail={contactEmail}
        contactPhone={contactPhone}
        contactEmailError={contactEmailError}
        contactPhoneError={contactPhoneError}
        onContactEmailChange={onContactEmailChange}
        onContactPhoneChange={onContactPhoneChange}
      />

      <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
        <div className="min-w-0">
          <FieldLabel>Consultor responsável</FieldLabel>
          <Select
            value={consultantValue}
            options={consultantsOptions}
            placeholder="Selecione"
            styles={descriptionSelectStyles}
            noOptionsMessage={() => (isFetchingConsultants ? "Carregando" : "")}
            onChange={(e) => onConsultantChange(e?.value)}
          />
        </div>
        <div className="min-w-0">
          <FieldLabel>Captador</FieldLabel>
          <Select
            value={finderValue}
            options={findersOptions}
            placeholder="Selecione"
            styles={descriptionSelectStyles}
            noOptionsMessage={() =>
              loadingFinders ? "Carregando" : "Nenhum encontrado"
            }
            onChange={(e) => onFinderChange(e?.value)}
          />
        </div>
      </div>
      <div className="mt-4">
        <KeyLocationFields
          catalog={keyLocationCatalog}
          keyLocationId={keyLocationId}
          keyLocationNote={keyLocationNote}
          keyLocationPassword={keyLocationPassword}
          onKeyLocationChange={onKeyLocationChange}
          onKeyLocationNoteChange={onKeyLocationNoteChange}
          onKeyLocationPasswordChange={onKeyLocationPasswordChange}
        />
      </div>
    </FormSection>
  );
}
